import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, ExternalLink, Loader2, Pause, Play, Sparkles, Undo2, X } from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@farmos/ui/button';
import { cn } from '@farmos/ui/utils';
import { api, ApiError } from '@/api';
import { supabase } from '@/supabase';
import {
  type Action,
  type Attachment,
  attachmentsOf,
  type Message,
  putAction,
  putMessage,
  useMediaUrl,
} from './data';

const time = (iso: string, lang: string) =>
  new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

/** FarmAI's face: the brand gradient with a spark. */
export function Avatar({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'grid size-8 shrink-0 place-items-center rounded-full bg-[linear-gradient(135deg,var(--hero-from),var(--hero-to))] text-white shadow-card',
        className,
      )}
      aria-hidden
    >
      <Sparkles className="size-[45%]" />
    </span>
  );
}

const AI_BUBBLE = 'rounded-[24px] rounded-ss-lg bg-panel px-4 py-3 shadow-card';
const MY_BUBBLE =
  'rounded-[24px] rounded-ee-lg bg-brand px-4 py-3 text-brand-ink shadow-[0_12px_24px_-14px_var(--brand)]';

/** One message: FarmAI's on the start side next to its avatar, the user's on the end side. */
function Row({ mine, at, children }: { mine?: boolean; at?: string; children: ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <div className={cn('flex items-start gap-2', mine && 'justify-end')}>
      {!mine && <Avatar className="mt-1" />}
      <div
        className={cn(
          'flex min-w-0 max-w-[85%] flex-col gap-1.5 sm:max-w-[70%]',
          mine ? 'items-end' : 'items-start',
        )}
      >
        {children}
        {at && <time className="px-2 text-[11px] text-muted">{time(at, i18n.language)}</time>}
      </div>
    </div>
  );
}

function Text({ children }: { children: string }) {
  return (
    <p className="whitespace-pre-wrap break-words leading-relaxed" dir="auto">
      {children}
    </p>
  );
}

function Photo({ path, className }: { path: string; className?: string }) {
  const url = useMediaUrl(path).data;
  return url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('overflow-hidden rounded-[22px]', className)}
    >
      <img src={url} alt="" className="size-full rounded-[22px] object-cover" />
    </a>
  ) : (
    <div className={cn('skeleton rounded-[22px]', className)} />
  );
}

// Up to three photos lie on top of each other like prints on a table.
const PILE = [
  'start-0 top-10 -rotate-6 z-10',
  'end-0 top-0 rotate-3 z-0',
  'end-3 bottom-0 -rotate-1 z-20',
];

function Photos({ paths }: { paths: string[] }) {
  if (paths.length === 1)
    return <Photo path={paths[0]!} className="block h-60 w-56 shadow-pop ring-4 ring-panel" />;
  return (
    <div className="relative h-80 w-64">
      {paths.slice(0, 3).map((p, i) => (
        <Photo
          key={p}
          path={p}
          className={cn('absolute size-40 rounded-[22px] shadow-pop ring-4 ring-panel', PILE[i])}
        />
      ))}
      {paths.length > 3 && (
        <span className="absolute bottom-2 start-2 z-30 rounded-full bg-panel px-2.5 py-1 text-xs font-semibold shadow-card">
          +{paths.length - 3}
        </span>
      )}
    </div>
  );
}

/** Bar heights for a voice note: the same pattern for the same file, so it doesn't jump. */
function waveform(seed: string, bars = 30) {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return Array.from({ length: bars }, (_, i) => {
    h = (h * 1103515245 + 12345) >>> 0;
    // louder in the middle of a phrase, like speech
    const envelope = Math.sin((Math.PI * (i + 1)) / (bars + 1));
    return Math.round(25 + 75 * envelope * (0.45 + (h % 1000) / 1800));
  });
}

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/** A voice note as a pill: play, the waveform filling as it plays, and how long it is. */
function Voice({ a }: { a: Attachment }) {
  const { t } = useTranslation();
  const url = useMediaUrl(a.path).data;
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const bars = useMemo(() => waveform(a.path), [a.path]);
  const seconds = (a.duration_ms ?? 0) / 1000;

  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  };

  return (
    <div className={cn(MY_BUBBLE, 'rounded-full py-2 ps-2 pe-4')}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          disabled={!url}
          aria-label={playing ? t('chat.pause') : t('chat.play')}
          className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-ink text-brand disabled:opacity-60"
        >
          {playing ? (
            <Pause className="size-4 fill-current" />
          ) : (
            <Play className="size-4 fill-current" />
          )}
        </button>
        <span className="flex h-8 items-center gap-[2px]" aria-hidden>
          {bars.map((h, i) => (
            <span
              key={i}
              className={cn(
                'w-[3px] rounded-full bg-current transition-opacity',
                (i + 1) / bars.length > progress && 'opacity-45',
              )}
              style={{ height: `${h}%` }}
            />
          ))}
        </span>
        <span className="text-xs tabular-nums">
          {mmss(playing || progress ? seconds * progress : seconds)}
        </span>
      </div>
      {url && (
        // no caption track: the transcript is shown with the message
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audio}
          src={url}
          preload="none"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => {
            const el = e.currentTarget;
            if (el.duration) setProgress(el.currentTime / el.duration);
          }}
          onEnded={() => setProgress(0)}
        />
      )}
    </div>
  );
}

function useActionCall(verb: 'confirm' | 'reject' | 'undo', action: Action) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ action: Action; message?: Message }>(`/actions/${action.id}/${verb}`, {}),
    onSuccess: (r) => {
      putAction(qc, r.action);
      if (r.message) putMessage(qc, r.message);
    },
    onError: (e) => {
      toast.error(
        e instanceof ApiError && e.message === 'expired' ? t('action.expired') : t('common.error'),
      );
      void qc.invalidateQueries({ queryKey: ['actions', action.thread_id] });
    },
  });
}

// The database stores the preview as jsonb, which doesn't keep key order.
const FIELD_ORDER = [
  'task',
  'title',
  'date',
  'from_date',
  'team',
  'greenhouse',
  'station',
  'start',
  'end',
  'workers',
  'owner',
  'due_date',
  'priority',
  'status',
  'photos',
  'body',
  'note',
  'overlaps',
];
const rank = (k: string) => FIELD_ORDER.indexOf(k) + 1 || 99;

function Value({ k, v }: { k: string; v: unknown }) {
  const { t, i18n } = useTranslation();
  if (k === 'status' || k === 'priority') return <>{t(`value.${String(v)}`)}</>;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
    return (
      <bdi>
        {new Intl.DateTimeFormat(i18n.language, {
          weekday: 'short',
          day: 'numeric',
          month: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(`${v}T00:00:00Z`))}
      </bdi>
    );
  return <bdi>{String(v)}</bdi>;
}

/** A change FarmAI prepared. Nothing is saved until the user taps Confirm. */
export function ActionCard({ action, at }: { action: Action; at?: string }) {
  const { t } = useTranslation();
  const confirm = useActionCall('confirm', action);
  const reject = useActionCall('reject', action);
  const undo = useActionCall('undo', action);
  const expired = action.status === 'proposed' && action.expires_at < new Date().toISOString();
  const status = expired ? 'expired' : action.status;
  const preview = Object.entries((action.preview ?? {}) as Record<string, unknown>)
    .filter(([, v]) => v != null && v !== '')
    .sort(([a], [b]) => rank(a) - rank(b));
  const link = (action.result as { link?: string } | null)?.link;
  const busy = confirm.isPending || reject.isPending || undo.isPending;

  return (
    <Row at={at}>
      <article
        className={cn(
          'w-[min(22rem,78vw)] overflow-hidden rounded-[26px] rounded-ss-lg bg-panel shadow-card',
          status === 'proposed' && 'ring-1 ring-brand/30',
        )}
      >
        <header className="flex items-center gap-2.5 px-4 pb-1 pt-4">
          <span className="grid size-8 place-items-center rounded-full bg-brand-soft text-brand-2">
            <Sparkles className="size-4" aria-hidden />
          </span>
          <h3 className="font-semibold">
            {t(`tool.${action.tool}`, { defaultValue: action.tool })}
          </h3>
        </header>
        <dl className="mx-4 my-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-[18px] bg-tile px-4 py-3 text-sm">
          {preview.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className={cn('text-muted', k === 'overlaps' && 'text-gap')}>
                {t(`field.${k}`, { defaultValue: k })}
              </dt>
              <dd
                className={cn('min-w-0 break-words font-medium', k === 'overlaps' && 'text-gap')}
                dir="auto"
              >
                <Value k={k} v={v} />
              </dd>
            </div>
          ))}
        </dl>
        <footer className="flex flex-wrap items-center gap-2 px-4 pb-4">
          {status === 'proposed' ? (
            <>
              <Button className="flex-1" disabled={busy} onClick={() => confirm.mutate()}>
                {confirm.isPending ? <Loader2 className="animate-spin" /> : <Check />}
                {t('action.confirm')}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => reject.mutate()}>
                <X /> {t('action.cancel')}
              </Button>
            </>
          ) : (
            <>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium',
                  status === 'executed'
                    ? 'bg-leaf/10 text-leaf'
                    : status === 'failed'
                      ? 'bg-gap/10 text-gap'
                      : 'bg-soft text-muted',
                )}
              >
                {status === 'executing' && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {status === 'executed' && <Check className="size-4" aria-hidden />}
                {t(`action.${status}`)}
              </span>
              {status === 'executed' && (
                <span className="ms-auto flex gap-1">
                  {link && (
                    <Button size="sm" variant="ghost" asChild>
                      <a href={link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink /> {t('action.open_farmos')}
                      </a>
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" disabled={busy} onClick={() => undo.mutate()}>
                    <Undo2 className="rtl:-scale-x-100" /> {t('action.undo')}
                  </Button>
                </span>
              )}
              {status === 'failed' && action.error && (
                <span className="w-full px-1 text-xs text-muted" dir="auto">
                  {action.error}
                </span>
              )}
            </>
          )}
        </footer>
      </article>
    </Row>
  );
}

function Notification({ m }: { m: Message }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ack = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('ack_notification', { p_message: m.id });
      if (error) throw error;
    },
    onSuccess: () => putMessage(qc, { ...m, acknowledged_at: new Date().toISOString() }),
  });
  return (
    <Row at={m.created_at}>
      <div className={cn(AI_BUBBLE, 'ring-1 ring-brand/25')}>
        <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-2.5 py-0.5 text-xs font-semibold text-brand-2">
          <Bell className="size-3.5" aria-hidden /> FarmAI
        </div>
        <Text>{m.body}</Text>
        <div className="mt-3">
          {m.acknowledged_at ? (
            <span className="text-xs font-medium text-leaf">✓ {t('chat.seen')}</span>
          ) : (
            <Button size="sm" variant="soft" disabled={ack.isPending} onClick={() => ack.mutate()}>
              {t('chat.got_it')}
            </Button>
          )}
        </div>
      </div>
    </Row>
  );
}

export function MessageView({ m, action }: { m: Message; action?: Action }) {
  const { t } = useTranslation();
  if (m.kind === 'action') return action ? <ActionCard action={action} at={m.created_at} /> : null;
  // the card itself shows the outcome
  if (m.kind === 'action_result') return null;
  if (m.kind === 'notification') return <Notification m={m} />;
  const mine = m.role === 'user';
  const atts = attachmentsOf(m);
  const photos = atts.filter((a) => a.mime.startsWith('image/')).map((a) => a.path);
  const voices = atts.filter((a) => a.mime.startsWith('audio/'));
  return (
    <Row mine={mine} at={m.created_at}>
      {photos.length > 0 && <Photos paths={photos} />}
      {voices.map((a) => (
        <div key={a.path} className="flex max-w-full flex-col items-end gap-1">
          <Voice a={a} />
          <p className="max-w-full px-3 text-sm text-muted" dir="auto">
            {a.transcript ?? t('chat.transcribing')}
          </p>
        </div>
      ))}
      {m.body && (
        <div className={mine ? MY_BUBBLE : AI_BUBBLE}>
          <Text>{m.body}</Text>
        </div>
      )}
    </Row>
  );
}

/** FarmAI's reply while it is still being written; three dots until the first words. */
export function Streaming({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <div aria-live="polite">
      <Row>
        <div className={AI_BUBBLE}>
          {text ? (
            <Text>{text}</Text>
          ) : (
            <span
              className="flex h-6 items-center gap-1"
              role="status"
              aria-label={t('chat.thinking')}
            >
              {[0, 150, 300].map((d) => (
                <span
                  key={d}
                  className="size-2 animate-bounce rounded-full bg-muted"
                  style={{ animationDelay: `${d}ms` }}
                />
              ))}
            </span>
          )}
        </div>
      </Row>
    </div>
  );
}
